import { Component, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { AppStateService, RequestService } from "@services/index";
import { clearLocalStorageUtil } from "@utils/clear-local-storage.util";

export interface PrintLabelsDialogData {
  /** The orders to print labels for: every merchant's parcel of each. */
  orderIds: string[];
  /** Only this merchant's parcel. */
  vendorId?: number;
}

/**
 * Print shipping labels (the merchant portal has the same dialog): how many parcels (one label each, "1 of 2"...) and
 * on what paper, then the PDF opens in a new tab to print. The tab is opened
 * on the tap itself, so browsers do not block it as a pop-up (if one does, the PDF
 * downloads instead).
 */
@Component({
  selector: "fh-print-labels-dialog",
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
  ],
  template: `
    <h2 mat-dialog-title>Print shipping label{{ data.orderIds.length > 1 ? "s" : "" }}</h2>
    <mat-dialog-content>
      <p class="hint">
        {{ data.orderIds.length }} order{{ data.orderIds.length > 1 ? "s" : "" }} - one label per merchant
        parcel; Singapore and US orders; walk-in items are left off. US parcels still need the
        carrier's postage label.
      </p>
      <mat-form-field appearance="outline" class="parcels">
        <mat-label>Parcels per order</mat-label>
        <input matInput type="number" min="1" max="20" [(ngModel)]="parcels" />
        <mat-hint>One label per box: "1 of 2", "2 of 2"</mat-hint>
      </mat-form-field>
      <p class="label">Paper</p>
      <mat-radio-group [(ngModel)]="paper" class="paper">
        <mat-radio-button value="label">Label printer - 100 x 150 mm</mat-radio-button>
        <mat-radio-button value="a4">A4 - one label per page</mat-radio-button>
      </mat-radio-group>
      @if (error()) {
        <p class="error">{{ error() }}</p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button mat-flat-button color="primary" [disabled]="busy()" (click)="print()">
        {{ busy() ? "Preparing..." : "Print" }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .hint { color: #6b7280; font-size: 13px; margin-top: 0; }
      .parcels { width: 100%; margin-top: 8px; }
      .label { margin: 16px 0 4px; font-weight: 500; }
      .paper { display: flex; flex-direction: column; gap: 6px; }
      .error { color: #b91c1c; font-size: 13px; margin-top: 12px; }
    `,
  ],
})
export class PrintLabelsDialogComponent {
  parcels = 1;
  paper: "label" | "a4" = "label";
  busy = signal(false);
  error = signal("");

  readonly data = inject<PrintLabelsDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<PrintLabelsDialogComponent>);
  private requestService = inject(RequestService);
  private appStateService = inject(AppStateService);

  print(): void {
    const parcels = Math.min(Math.max(Math.floor(Number(this.parcels)) || 1, 1), 20);
    // Opened now, on the tap, then pointed at the PDF once it is ready.
    const tab = window.open("", "_blank");
    this.busy.set(true);
    this.error.set("");
    const ids = this.data.orderIds.map(encodeURIComponent).join(",");
    this.requestService
      .getBlob(
        `/api/v2/shipping-labels?orderIds=${ids}&parcels=${parcels}&paper=${this.paper}` +
          (this.data.vendorId ? `&vendorId=${this.data.vendorId}` : "")
      )
      .subscribe({
        next: async (blob) => {
          // The server answers 200 even on failure, with a JSON blob instead of the PDF.
          if (blob?.type !== "application/pdf") {
            tab?.close();
            this.busy.set(false);
            const message = await this.messageOf(blob);
            if (message === "Unauthorized") return this.toLogin();
            this.error.set(message);
            return;
          }
          this.busy.set(false);
          const url = URL.createObjectURL(blob);
          if (tab) {
            tab.location.href = url;
          } else {
            // Pop-up blocked: download the PDF instead of navigating the app away.
            const link = document.createElement("a");
            link.href = url;
            link.download = "shipping-labels.pdf";
            document.body.appendChild(link);
            link.click();
            link.remove();
          }
          // Freed once the tab has had time to load it.
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
          this.dialogRef.close(true);
        },
        error: async (err) => {
          tab?.close();
          this.busy.set(false);
          // The server explains in JSON; it arrives as a blob here.
          const message = await this.messageOf(err?.error);
          if (message === "Unauthorized") return this.toLogin();
          this.error.set(message);
        },
      });
  }

  /**
   * Session expired: the interceptor can't see the "Unauthorized" inside a blob, so do
   * what it does for JSON replies - clear the client state and re-bootstrap at /login.
   */
  private toLogin(): void {
    clearLocalStorageUtil();
    this.appStateService.userToken$.next("");
    this.appStateService.currentUser$.next(null);
    window.location.href = "/login";
  }

  /** The server's JSON message inside a blob, or a general one. */
  private async messageOf(blob: unknown): Promise<string> {
    const fallback = "The labels could not be made";
    try {
      const body = JSON.parse(await (blob as Blob).text());
      return body?.message || fallback;
    } catch {
      return fallback;
    }
  }
}
